import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage, firebaseConfig, usingEmulators } from "./firebase";
import seed from "../data/seed.json";
import { slugify } from "../lib/format";
import { resizeImage } from "../lib/image";

const TIMEOUT_MS = 8000;

export const COLLECTIONS = {
  projects: "gradjevinski_projekti",
  reviews: "recenzije",
  messages: "poruke",
  units: (projectId) => `stanovi/${projectId}/stanovi`,
};

function withTimeout(promise, ms = TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Isteklo vreme za učitavanje podataka")), ms)
    ),
  ]);
}

async function readCollection(path) {
  const snapshot = await withTimeout(getDocs(collection(db, path)));
  // Legacy documents carry their own "id" field (e.g. 1, 2) that doesn't match the
  // document ID, so the document ID must win.
  return snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
}

// Legacy records store only a file name ("IMG_3530.jpg"); turn it into a public URL.
export function resolveImage(value, legacyFolder = "beocinski_trg/ostalo") {
  if (!value || typeof value !== "string") return null;
  if (/^(https?:|data:|blob:)/.test(value) || value.startsWith("/")) return value;
  const path = value.includes("/") ? value : `${legacyFolder}/${value}`;
  const base = usingEmulators
    ? `http://${process.env.REACT_APP_EMULATOR_HOST || "127.0.0.1"}:9199`
    : "https://firebasestorage.googleapis.com";
  return `${base}/v0/b/${firebaseConfig.storageBucket}/o/${encodeURIComponent(path)}?alt=media`;
}

const seedId = ({ _id, ...rest }) => ({ id: _id, ...rest });

// "1200 eur + PDV" -> "1.200 €/m² + PDV"
export function normalizePrice(value) {
  if (!value) return "";
  return String(value)
    .replace(/(\d)(?=(\d{3})(?!\d))/g, (m, d, offset, str) => (/[.,]/.test(str[offset - 1] || "") ? m : `${d}.`))
    .replace(/\s*(eur|evra|€)(?!\s*\/)/i, " €/m²")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeProject(raw) {
  const identifier = raw.identifier || raw.naziv || "";
  return {
    ...raw,
    identifier,
    najniza_cena: normalizePrice(raw.najniza_cena),
    najvisa_cena: normalizePrice(raw.najvisa_cena),
    slug: raw.slug || slugify(identifier) || raw.id,
    redosled: Number(raw.redosled ?? 0),
    slike: (raw.slike || []).map((s) => resolveImage(s)).filter(Boolean),
    kapacitet_stanovi: Number(raw.kapacitet_stanovi) || 0,
    kapacitet_lokali: Number(raw.kapacitet_lokali) || 0,
    zauzetost_stanovi: Number(raw.zauzetost_stanovi) || 0,
    zauzetost_lokali: Number(raw.zauzetost_lokali) || 0,
  };
}

function normalizeUnit(raw, projectId) {
  return {
    ...raw,
    projectId,
    broj: String(raw.broj ?? ""),
    tip: raw.tip || "stan",
    status: raw.status || "slobodan",
    imageUrls: (raw.imageUrls || [])
      .map((s) => resolveImage(s, `stanovi/${projectId}`))
      .filter(Boolean),
  };
}

const sortProjects = (list) =>
  [...list].sort((a, b) => a.redosled - b.redosled || a.identifier.localeCompare(b.identifier));

/* ---------- public site ---------- */

export async function getProjects() {
  try {
    const live = await readCollection(COLLECTIONS.projects);
    if (live.length) {
      return sortProjects(live.filter((p) => p.u_prodaji !== false).map(normalizeProject));
    }
  } catch (error) {
    console.warn("Projekti: koristim rezervne podatke.", error.message);
  }
  return sortProjects(
    seed.gradjevinski_projekti.map((p) => ({ ...normalizeProject(seedId(p)), fallback: true }))
  );
}

export async function getUnits(project) {
  if (!project) return [];
  const fromSeed = () =>
    (seed.stanovi[project.id] || []).map((u) => normalizeUnit(seedId(u), project.id));
  if (project.fallback) return fromSeed();
  try {
    const live = await readCollection(COLLECTIONS.units(project.id));
    return live.map((u) => normalizeUnit(u, project.id));
  } catch (error) {
    console.warn("Stanovi: koristim rezervne podatke.", error.message);
    return fromSeed();
  }
}

export async function getAllUnits(projects) {
  const lists = await Promise.all(projects.map((p) => getUnits(p)));
  return lists.flat();
}

export async function getReviews() {
  let list;
  try {
    list = await readCollection(COLLECTIONS.reviews);
  } catch (error) {
    console.warn("Recenzije: koristim rezervne podatke.", error.message);
    list = seed.recenzije.map(seedId);
  }
  return list
    .filter((r) => r.objavljeno !== false && r.text)
    .map((r) => ({ ...r, cover: resolveImage(r.cover, "reviews") }));
}

export async function sendMessage({ ime, email, telefon, poruka, izvor, stan, interesovanje, jezik }) {
  const data = {
    ime: ime.trim(),
    email: (email || "").trim(),
    telefon: telefon.trim(),
    poruka: (poruka || "").trim(),
    izvor: izvor || "sajt",
    procitano: false,
    timestamp: serverTimestamp(),
  };
  if (stan) data.stan = stan;
  if (interesovanje) data.interesovanje = interesovanje;
  // Language the visitor used, so the office replies in it (only non-default languages matter).
  if (["sr", "en", "ru"].includes(jezik)) data.jezik = jezik;
  return withTimeout(addDoc(collection(db, COLLECTIONS.messages), data), 15000);
}

/* ---------- admin ---------- */

export const adminList = (path) => readCollection(path);

export async function adminListProjects() {
  return sortProjects((await readCollection(COLLECTIONS.projects)).map((p) => ({
    ...p,
    identifier: p.identifier || p.naziv || "",
    redosled: Number(p.redosled ?? 0),
  })));
}

export async function adminSave(path, id, data) {
  const clean = Object.fromEntries(
    Object.entries(data).filter(([key, v]) => key !== "id" && v !== undefined)
  );
  if (id) {
    await updateDoc(doc(db, path, id), clean);
    return id;
  }
  const created = await addDoc(collection(db, path), clean);
  return created.id;
}

export const adminSet = (path, id, data) => setDoc(doc(db, path, id), data);
export const adminUpdate = (path, id, data) => updateDoc(doc(db, path, id), data);
export const adminDelete = (path, id) => deleteDoc(doc(db, path, id));

export async function uploadImage(file, folder) {
  const prepared = await resizeImage(file);
  const safeName = slugify(file.name.replace(/\.[^.]+$/, "")) || "slika";
  const ext = prepared.type === "image/webp" ? "webp" : prepared.type === "image/png" ? "png" : "jpg";
  const storageRef = ref(storage, `${folder}/${Date.now()}_${safeName}.${ext}`);
  await uploadBytes(storageRef, prepared, {
    contentType: prepared.type,
    cacheControl: "public, max-age=31536000",
  });
  return getDownloadURL(storageRef);
}

// Deleting from Storage is best-effort; the database record is what matters.
export async function deleteImage(url) {
  if (!url || !url.includes(`/b/${firebaseConfig.storageBucket}/`)) return;
  try {
    await deleteObject(ref(storage, url));
  } catch (error) {
    console.warn("Slika nije obrisana iz Storage-a:", error.message);
  }
}
